// docs/61 EX14, docs/138 in the encounter HUD: the exploration's card and its briefing.
//
// The CARD (right column): the place's name and why the party is here, where the selected (else
// first) hero stands, the objective — the one wonder, unknown, seen (named) or found — the side
// objective with the finds the party has picked up, the camps seen (asleep or roused), the vaults
// found (chest opened or not), finders keepers said plainly, "About this place" (the briefing
// again), and the journal of everything found (the wonder's words, its omen apart, lore read,
// vaults' words). Every word is the sim's or the storyteller's (`Snapshot.explore` and
// `Snapshot.crawl`, the party's view); this file lays them out. It also asks the music director
// for the air's mood (tension while a camp is up and fighting).
//
// The BRIEFING (docs/138 XO9): before the first turn, a card over the board — the place and its
// air, why you are here, what this place is, how it ties in, the objective and finders keepers.
// Read once per encounter per browser; the card's "About this place" opens it again.
import { useEffect, useRef, useState } from 'react';
import { Badge, Button, Modal, OryxIcon, Reveal } from '../components';
import { useEncounter, useShownSnapshot } from '../../core/encounter/session';
import { useAuthoring } from '../../core/stores/authoring';
import { useExplore } from '../../core/stores/explore';
import { deviceNow } from '../../core/time';
import { whereIs } from '../../core/crawl/crawl';
import { FINDERS_KEEPERS, SIDE_LINE, airOf, briefParts, exploreMood, journal, lookOf, progress, seenCamps, seenVaults, type JournalEntry } from '../../core/explore/explore';
import { useMusicMood } from '../../core/audio/music';
import type { Snapshot } from '../../core/sim/types';
import './crawl.css';
import './explore.css';

/** The air's music while the card is up (the HUD mounts the card for an exploration only). */
function ExploreMusic({ snap }: { snap: Snapshot }) {
  useMusicMood(exploreMood(snap));
  return null;
}

/** The card itself, for the HUD and the `?explore=1` gallery (which passes its own board). */
export function ExploreCard({ board, latest, compact = false, music = true }: { board?: Snapshot; latest?: Snapshot; compact?: boolean; music?: boolean }) {
  const shown = useShownSnapshot();
  const now = useEncounter((s) => s.snapshot);
  const encounterId = useEncounter((s) => s.encounterId);
  const selected = useAuthoring((s) => s.selectedHero);
  const openBrief = useExplore((s) => s.openBrief);
  const snap = board ?? shown;
  const [open, setOpen] = useState(!compact);
  const x = snap?.explore;
  if (!snap || !x) return null;
  // the journal reads the latest board: the playback flashes a wonder as it is beheld
  const journalSnap = latest ?? now ?? snap;
  const crawl = snap.crawl;
  const hero = snap.units.find((u) => u.heroId !== undefined && u.heroId === selected && u.onBoard !== false) ?? snap.units.find((u) => u.heroId !== undefined && u.onBoard !== false);
  const where = hero && crawl ? whereIs(crawl, snap.board.w, hero.tile) : {};
  const p = progress(snap);
  const air = airOf(x.air);
  const camps = seenCamps(x, crawl, snap.board.w);
  const vaults = seenVaults(snap);
  const left = crawl ? Math.max(0, crawl.pulseLimit - snap.pulseIndex) : undefined;
  const wonder = x.wonders.find((w) => w.found) ?? x.wonders.find((w) => w.name);
  const wonderWord = p.wonder === 'found' ? `found: ${p.wonderName}` : p.wonder === 'seen' ? `seen: ${p.wonderName}` : 'not yet seen';
  return (
    <section className={`fk-hud__crawl fk-hud__explore fk-hit is-air-${air.key.toLowerCase()}`} data-testid="explore-card" aria-label={`Exploring ${crawl?.name ?? ''}`}
      data-wonder={p.wonder} data-finds={p.finds} data-camps={`${p.roused}/${p.camps}`} data-vaults={`${p.vaultsOpened}/${p.vaults}`} data-air={air.key} data-reach={where.depth?.index}>
      {music && !board && <ExploreMusic snap={snap} />}
      <button type="button" className="fk-hud__crawl-head" onClick={() => setOpen(!open)} aria-expanded={open} data-testid="explore-toggle">
        <h3 className="fk-hud__label">{crawl?.name || 'The unknown'}</h3>
        <span className="fk-hud__crawl-count" title={`The wonder: ${wonderWord}`}>✦ {p.wonder === 'found' ? 'found' : p.wonder === 'seen' ? 'seen' : '?'}</span>
        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>
      {open && <>
        {crawl?.hook && <p className="fk-hud__crawl-hook" data-testid="explore-hook">{crawl.hook}</p>}
        <p className="fk-hud__explore-air" title={air.words}><span aria-hidden="true">≋</span> {air.words}</p>
        {crawl && <div className="fk-hud__crawl-where" data-testid="explore-where">
          <span className="fk-hud__crawl-eyebrow">{hero ? `${hero.name} is in` : 'The party is in'}</span>
          <b className="fk-hud__crawl-depth">{where.depth?.name || (where.depth ? `Reach ${where.depth.index + 1}` : 'the mist')}</b>
          {where.area?.name && <span className="fk-hud__crawl-room">{where.area.name}</span>}
        </div>}
        {/* docs/138 XO3: the objective, then the side objective */}
        <div className={`fk-hud__explore-goal is-${p.wonder}`} data-testid="explore-wonder" data-found={p.wonder === 'found'}>
          <span className="fk-hud__explore-gicon">{wonder && (p.wonder !== 'unknown') ? <OryxIcon name={lookOf(wonder.look).frame} scale={1} title={cap(p.wonderName)} /> : <span className="fk-hud__explore-q" aria-hidden="true">?</span>}</span>
          <span className="fk-hud__explore-gtext">
            <span className="fk-hud__crawl-eyebrow">Objective · find the wonder</span>
            <b>{p.wonder === 'found' ? `✦ Found: ${cap(p.wonderName)}` : p.wonder === 'seen' ? `Seen: ${cap(p.wonderName)}` : 'Somewhere in the mist'}</b>
            {p.wonder === 'found' && <small>Every hero may Behold it once for a blessing.</small>}
            {p.wonder === 'seen' && <small>Walk beside it and Behold it.</small>}
          </span>
        </div>
        <div className="fk-hud__explore-side" data-testid="explore-side" data-finds={p.finds}>
          <span className="fk-hud__crawl-eyebrow">Side · hidden loot</span>
          <span>{p.finds ? <><b>{p.finds}</b> find{p.finds === 1 ? '' : 's'} picked up</> : 'Nothing picked up yet'}{p.findsLying > 0 && <> · <b>{p.findsLying}</b> found, still lying there</>}</span>
          <small>Search shows hidden things within three squares; walk onto a find to pick it up.</small>
        </div>
        {camps.length > 0 && (
          <ul className="fk-hud__explore-list" aria-label="Camps seen" data-testid="explore-camps">
            {camps.map(({ camp, index, roused }) => (
              <li key={`c${index}`} className={roused ? 'is-roused' : 'is-asleep'} title={camp.idle || undefined} data-testid="explore-camp" data-roused={roused}>
                <span aria-hidden="true">{roused ? '⚔' : '☾'}</span> <span className="fk-hud__explore-lname">{cap(camp.name)}</span>
                <Badge tone={roused ? 'red' : 'grey'}>{roused ? 'roused' : 'keeping to itself'}</Badge>
              </li>
            ))}
          </ul>
        )}
        {vaults.length > 0 && (
          <ul className="fk-hud__explore-list" aria-label="Vaults found" data-testid="explore-vaults">
            {vaults.map(({ vault, index, opened }) => (
              <li key={`v${index}`} className={opened ? 'is-opened' : 'is-sealed'} title={vault.rule || undefined} data-testid="explore-vault" data-opened={opened}>
                <span aria-hidden="true">⌂</span> <span className="fk-hud__explore-lname">{cap(vault.name || 'a sealed vault')}</span>
                <Badge tone={opened ? 'gold' : 'grey'}>{opened ? 'chest opened' : 'chest sealed'}</Badge>
              </li>
            ))}
          </ul>
        )}
        {/* docs/138 XO4: the one encounter whose spoils are personal */}
        <p className="fk-hud__explore-keepers" data-testid="explore-keepers"><b>Finders keepers.</b> What each hero picks up is theirs alone. Experience is shared.</p>
        <p className="fk-hud__crawl-stats">
          {p.omens > 0 && <Badge tone="gold" title="Clues for puzzles still to come — they are kept in the World">{p.omens} omen{p.omens === 1 ? '' : 's'} kept</Badge>}{' '}
          {left !== undefined && left < 40 && <Badge tone="red">{left} turns left</Badge>}{' '}
          {encounterId !== undefined && !board && <Button small variant="ghost" onClick={() => openBrief(encounterId)} data-testid="explore-about">About this place</Button>}
        </p>
        <Journal snap={journalSnap} name={crawl?.name ?? ''} />
      </>}
    </section>
  );
}

const KIND_MARK: Record<JournalEntry['kind'], string> = { wonder: '✦', omen: '☽', lore: '✎', vault: '⌂' };

/** The journal: the wonder beheld, its omen, lore read and vaults found, in the order they lie. */
function Journal({ snap, name }: { snap: Snapshot; name: string }) {
  const open = useExplore((s) => s.journalOpen);
  const flash = useExplore((s) => s.flash);
  const setOpen = useExplore((s) => s.setJournal);
  const [shown, setShown] = useState<string>();
  const [book, setBook] = useState(false);
  const lit = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!flash || lit.current === flash.at) return;
    lit.current = flash.at;
    setShown(`wonder:${flash.wonder}`);
  }, [flash]);
  const entries = journal(snap);
  const fresh = flash && deviceNow() - flash.at < 6000 ? flash.wonder : undefined;
  return (
    <div className="fk-hud__crawl-journal" data-testid="explore-journal" data-entries={entries.length}>
      <div className="fk-hud__crawl-jhead">
        <button type="button" className="fk-hud__crawl-jtoggle" onClick={() => setOpen(!open)} aria-expanded={open} data-testid="explore-journal-toggle">
          {open ? '▾' : '▸'} Journal <span className="fk-hud__crawl-count">{entries.length}</span>
        </button>
        {entries.length > 0 && <Button small variant="ghost" onClick={() => setBook(true)} data-testid="explore-journal-book">Read all</Button>}
      </div>
      {open && (
        <ul className="fk-hud__crawl-lore">
          {entries.map((e) => {
            const key = `${e.kind}:${e.index}`;
            return (
              <li key={key} className={`fk-hud__crawl-entry is-read fk-hud__explore-e is-${e.kind} ${e.kind === 'wonder' && fresh === e.index ? 'is-fresh' : ''}`} data-testid="explore-entry" data-kind={e.kind}>
                <button type="button" className="fk-hud__crawl-etitle" onClick={() => setShown(shown === key ? undefined : key)} aria-expanded={shown === key}><span aria-hidden="true">{KIND_MARK[e.kind]}</span> {e.head}</button>
                {shown === key && e.text && <q className="fk-hud__crawl-etext">{e.text}</q>}
              </li>
            );
          })}
          {!entries.length && <li className="fk-hud__crawl-entry is-unread">Nothing found yet. Find the wonder, walk beside it and Behold it.</li>}
        </ul>
      )}
      <Modal open={book} onClose={() => setBook(false)} eyebrow="The journal" title={name || 'The unknown'} testId="explore-journal-modal" size="wide">
        <div className="fk-crawl-book">
          {entries.map((e) => (
            <article key={`${e.kind}:${e.index}`} className={`fk-crawl-book__entry fk-explore-book__${e.kind}`}>
              <h4>{KIND_MARK[e.kind]} {e.head}</h4>
              <p>{e.text}</p>
            </article>
          ))}
        </div>
      </Modal>
    </div>
  );
}

/**
 * docs/138 XO9: the briefing over the board. Up on its own before the first turn of an exploration
 * this browser has not read it for; again whenever the card's "About this place" asks. `board`
 * and `onClose` let the gallery show it without an encounter.
 */
export function BriefingCard({ board, onClose }: { board?: Snapshot; onClose?: () => void }) {
  const live = useEncounter((s) => s.snapshot);
  const encounterId = useEncounter((s) => s.encounterId);
  const briefFor = useExplore((s) => s.briefFor);
  useExplore((s) => s.rev);
  const snap = board ?? live;
  const x = snap?.explore;
  if (!snap || !x) return null;
  const asked = encounterId !== undefined && briefFor === encounterId;
  // the first turn the party plans is pulse 1 live (0 only in a gallery's Snapshot 0)
  const first = !board && encounterId !== undefined && snap.pulseIndex <= 1 && !useExplore.getState().briefSeen(encounterId);
  if (!board && !asked && !first) return null;
  const close = () => {
    if (onClose) { onClose(); return; }
    if (encounterId !== undefined) useExplore.getState().setBriefSeen(encounterId);
    useExplore.getState().closeBrief();
  };
  return <BriefingView snap={snap} onClose={close} again={asked} />;
}

/** The briefing card as a pure view. */
export function BriefingView({ snap, onClose, again = false }: { snap: Snapshot; onClose: () => void; again?: boolean }) {
  const x = snap.explore!;
  const crawl = snap.crawl;
  const air = airOf(x.air);
  const parts = briefParts(x);
  const p = progress(snap);
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', h, true);
    return () => window.removeEventListener('keydown', h, true);
  }, [onClose]);
  return (
    <div className="fk-brief" role="dialog" aria-modal="false" aria-labelledby="fk-brief-title" data-testid="explore-brief" data-parts={parts.length}>
      <div className="fk-brief__veil" aria-hidden="true" onClick={onClose} />
      <Reveal kind="pop" className={`fk-brief__card fk-hit is-air-${air.key.toLowerCase()}`}>
        <span className="fk-hud__opening-eyebrow">{again ? 'About this place' : 'An exploration'}</span>
        <h2 id="fk-brief-title" className="fk-hud__opening-title">{crawl?.name || 'The unknown'}</h2>
        <p className="fk-brief__air"><span aria-hidden="true">≋</span> {air.words}</p>
        {parts.length > 0
          ? parts.map((part) => (
            <section key={part.key} className={`fk-brief__part is-${part.key}`} data-testid={`brief-${part.key}`}>
              <h3>{part.head}</h3>
              <p>{part.text}</p>
            </section>
          ))
          : <section className="fk-brief__part is-why" data-testid="brief-why"><h3>Why you are here</h3><p>{crawl?.hook || 'The storyteller left no account of this place. Look about you: there is more here than meets the eye.'}</p></section>}
        <div className="fk-brief__rules">
          <section className="fk-brief__rule" data-testid="brief-objective">
            <h3>Objective</h3>
            <p><b>{p.wonder === 'found' ? `The wonder is found: ${p.wonderName}.` : 'Find the wonder.'}</b> {p.wonder === 'found' ? 'Leave when everyone is ready.' : 'Once it is found the field is yours: leave together when everyone is ready.'}</p>
            <p>{SIDE_LINE}</p>
          </section>
          <section className="fk-brief__rule is-keepers" data-testid="brief-keepers">
            <h3>Finders keepers</h3>
            <p>{FINDERS_KEEPERS.replace(/^Finders keepers: /, '').replace(/^w/, 'W')} Each hero who beholds the wonder chooses a blessing of their own.</p>
          </section>
        </div>
        <Button primary size="lg" onClick={onClose} data-testid="brief-close" data-autofocus>{again ? 'Close' : 'Set out'}</Button>
      </Reveal>
    </div>
  );
}

const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
